import React, { useState, useEffect, useGlobal, useDispatch } from "reactn";
import axios from "axios";
import { FiRefreshCw } from "react-icons/fi";
import BaseModal from "./baseModal/BaseModal";
import { SWITCH, GET_HTML, CONTENT_BLOCK } from "./baseModal/FieldTypes";
import { toast } from "react-toastify";
import ReshuffleModal from "./ReshuffleModal";

const AIEventInspirationModal = ({ onClose, history }) => {
  const global = useGlobal()[0];
  const setGlobal = useGlobal()[1];
  const [loading, setLoading] = useGlobal("loading");
  const dispatch = useDispatch();

  const [showReshuffleModal, setShowReshuffleModal] = useState(false);
  const [reshuffleMode, setReshuffleMode] = useState("name");
  
  const [superlativeData, setSuperlativeData] = useState({
    mostProfitable: [],
    mostEngaged: [],
    mostUsedSettings: {
      targetChildren: true,
      applicationRequired: false,
      offlinePayments: false,
      shareTransactionFee: false
    }
  });

  const [eventTitle, setEventTitle] = useState("Activating magic wizard...");
  const [eventDescription, setEventDescription] = useState("Activating magic wizard...");
  const [eventImage, setEventImage] = useState("");

  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [ellipsis, setEllipsis] = useState("...");
  const [isCreating, setIsCreating] = useState(false);

  const [addChild, setAddChild] = useState(false);
  const [manualInvoices, setManualInvoices] = useState(false);
  const [applicationRequired, setApplicationRequired] = useState(false);
  const [transactionCost, setTransactionCost] = useState(false);
  const [allowSameEnrollment, setAllowSameEnrollment] = useState(true);
  const [birthdayEvent] = useState(false); 

  useEffect(() => {
    if (isGeneratingSuggestions) {
      let i = 0;
      const seq = [".", "..", "..."];
      const id = setInterval(() => {
        i = (i + 1) % seq.length;
        setEllipsis(seq[i]);
      }, 500);
      return () => clearInterval(id);
    } else {
      setEllipsis("...");
    }
  }, [isGeneratingSuggestions]);

  useEffect(() => {
    if (global.organizationInfo && global.organizationInfo.partnerId) {
      loadAIAnalysis();
      generateAISuggestions();
    }
  }, [global.organizationInfo]);

  const loadAIAnalysis = async () => {
    try {
      if (!global.organizationInfo || !global.organizationInfo.partnerId) {
        console.warn("Partner ID not available yet");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API}/partners/events/ai-analysis/${global.organizationInfo.partnerId}`
      );
      
      if (response.data.success) {
        const analysisData = response.data.data;
        updateSuggestionsFromAnalysis(analysisData);
      }
    } catch (error) {
      console.error("Error loading AI analysis:", error);
    }
  };

  const updateSuggestionsFromAnalysis = (analysisData) => {
    if (!analysisData) return;

    if (Array.isArray(analysisData.mostProfitable)) {
      setSuperlativeData(prev => ({
        ...prev,
        mostProfitable: analysisData.mostProfitable
      }));
    }

    if (Array.isArray(analysisData.mostEngaged)) {
      setSuperlativeData(prev => ({
        ...prev,
        mostEngaged: analysisData.mostEngaged
      }));
    }

    if (analysisData.mostUsedSettings) {
      setSuperlativeData(prev => ({
        ...prev,
        mostUsedSettings: {
          targetChildren: analysisData.mostUsedSettings.targetChildren || true,
          applicationRequired: analysisData.mostUsedSettings.applicationRequired || false,
          offlinePayments: analysisData.mostUsedSettings.offlinePayments || false,
          shareTransactionFee: analysisData.mostUsedSettings.shareTransactionFee || false
        }
      }));
    }
  };

  const generateAISuggestions = async (constraints = {}) => {
    setIsGeneratingSuggestions(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/partners/events/openai-suggestions`,
        {
          partnerId: global.organizationInfo.partnerId,
          ...constraints
        }
      );

      if (response.data.success) {
        const s = response.data.data;
        setEventTitle(s.eventTitle);
        setEventDescription(s.eventDescription);
        setEventImage(s.eventImage);

        setAddChild(s.targetChildren);
        setManualInvoices(s.offlinePayments);
        setApplicationRequired(s.applicationRequired);
        setTransactionCost(s.shareTransactionFee);

        toast.success("AI suggestions generated successfully!");
      } else {
        toast.error(response.data.message || "Failed to generate AI suggestions");
      }
    } catch (error) {
      console.error("Error generating AI suggestions:", error);
      toast.error("Failed to generate AI suggestions. Please try again.");
    } finally {
      setIsGeneratingSuggestions(false);
    }
  };

  const shuffleSuggestions = () => {
    generateAISuggestions({ isShuffleRequest: true });
  };

  const shuffleSpecificField = async fieldType => {
    if (fieldType === "name" || fieldType === "description") {
      setReshuffleMode(fieldType === "name" ? "name" : "description");
      setShowReshuffleModal(true);
      return;
    }

    if (fieldType === "image") {
      setIsGeneratingSuggestions(true);
      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_API}/partners/events/openai-suggestions`,
          {
            partnerId: global.organizationInfo.partnerId,
            isShuffleRequest: true,
            shuffleType: "image"
          }
        );
        if (resp.data.success) {
          const s = resp.data.data;
          setEventImage(s.eventImage);
          toast.success("Event image shuffled!");
        } else {
          toast.error("Failed to shuffle. Please try again.");
        }
      } catch (e) {
        console.error("Error shuffling image:", e);
        toast.error("Failed to shuffle. Please try again.");
      } finally {
        setIsGeneratingSuggestions(false);
      }
    }
  };

  const handleApplyGeneratedName = selectedName => {
    setEventTitle(selectedName);
    toast.success("Event title applied!");
  };

  const handleApplyGeneratedDescription = selectedDesc => {
    setEventDescription(selectedDesc);
    toast.success("Event description applied!");
  };

  const handleImageUpload = async e => {
    if (e.target.files.length && e.target.files[0]) {
      const fd = new FormData();
      fd.set("image", e.target.files[0]);
      const ep = `${process.env.REACT_APP_API}/partners/forms/document`;
      setLoading(true);
      try {
        const result = await axios.post(ep, fd);
        if (result.data.success) {
          setEventImage(result.data.data);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
      }
      setLoading(false);
    }
  };

  const eventTitleWithShuffle = () => (
    <div style={{ marginBottom: "20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
        <div className="field-component-title">Event Title</div>
        <button
          onClick={() => shuffleSpecificField("name")}
          disabled={isGeneratingSuggestions}
          style={{
            padding: "4px 8px",
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: isGeneratingSuggestions ? "not-allowed" : "pointer",
            fontSize: "11px",
            whiteSpace: "nowrap",
            opacity: isGeneratingSuggestions ? 0.6 : 1
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <FiRefreshCw />
            Shuffle
          </span>
        </button>
      </div>
      <input
        type="text"
        value={
          isGeneratingSuggestions && /^activating magic wizard/i.test(eventTitle)
            ? `Activating magic wizard${ellipsis}`
            : eventTitle
        }
        onChange={e => setEventTitle(e.target.value)}
        placeholder="Enter event title"
        style={{
          width: "100%",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          fontSize: "14px"
        }}
      />
    </div>
  );

  const eventDescriptionWithShuffle = () => (
    <div style={{ marginBottom: "20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
        <div className="field-component-title">Event Description</div>
        <button
          onClick={() => shuffleSpecificField("description")}
          disabled={isGeneratingSuggestions}
          style={{
            padding: "4px 8px",
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: isGeneratingSuggestions ? "not-allowed" : "pointer",
            fontSize: "11px",
            whiteSpace: "nowrap",
            opacity: isGeneratingSuggestions ? 0.6 : 1
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <FiRefreshCw />
            Shuffle
          </span>
        </button>
      </div>
      <textarea
        value={
          isGeneratingSuggestions && /^activating magic wizard/i.test(eventDescription)
            ? `activating magic wizard${ellipsis}`
            : eventDescription
        }
        onChange={e => setEventDescription(e.target.value)}
        placeholder="Enter event description"
        style={{
          width: "100%",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          fontSize: "14px",
          minHeight: "120px",
          resize: "vertical"
        }}
      />
    </div>
  );

  const eventImageWithShuffle = () => (
    <div style={{ marginBottom: "20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
        <div className="field-component-title">Event Image</div>
        <button
          onClick={() => shuffleSpecificField("image")}
          disabled={isGeneratingSuggestions || isCreating}
          style={{
            padding: "4px 8px",
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: isGeneratingSuggestions || isCreating ? "not-allowed" : "pointer",
            fontSize: "11px",
            whiteSpace: "nowrap",
            opacity: isGeneratingSuggestions || isCreating ? 0.6 : 1
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <FiRefreshCw />
            Shuffle
          </span>
        </button>
      </div>

      <div
        style={{
          width: "100%",
          minHeight: 200,
          border: "1px solid #e3e3e3",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          background: "#fafafa"
        }}
      >
        {eventImage ? (
          <img
            src={eventImage}
            alt="Event preview"
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={{ color: "#999", fontSize: 12 }}>No image yet. Click Shuffle to generate.</div>
        )}
      </div>
    </div>
  );

  const renderTopProfitable = () => (
    <div style={{
      fontSize: "12px",
      color: "#666",
      fontWeight: "normal",
      display: "flex",
      flexDirection: "column",
      gap: "8px"
    }}>
      {superlativeData.mostProfitable.length === 0 ? (
        <div>No data available</div>
      ) : (
        superlativeData.mostProfitable.slice(0, 3).map((e, i) => (
          <div key={`prof-${i}`} style={{ marginBottom: "4px" }}>
            {e.name} | Revenue: {e.revenue ? `$${(e.revenue / 100).toLocaleString()}` : "$0"} | Enrollments: {e.enrollment || 0}
          </div>
        ))
      )}
    </div>
  );

  const renderTopEngaged = () => (
    <div style={{
      fontSize: "12px",
      color: "#666",
      fontWeight: "normal",
      display: "flex",
      flexDirection: "column",
      gap: "8px"
    }}>
      {superlativeData.mostEngaged.length === 0 ? (
        <div>No data available</div>
      ) : (
        superlativeData.mostEngaged.slice(0, 3).map((e, i) => (
          <div key={`eng-${i}`} style={{ marginBottom: "4px" }}>
            {e.name} | Enrollments: {e.enrollment || 0}
          </div>
        ))
      )}
    </div>
  );

  const getSuperlativeFields = () => {
    const hasRealData = (superlativeData.mostProfitable || []).length > 0 || (superlativeData.mostEngaged || []).length > 0;
    
    if (!hasRealData) {
      return [
        {
          type: CONTENT_BLOCK,
          data: { 
            name: "Organization Profile",
            content: "Since this is a new organization with no existing events, AI suggestions will be based on your organizational profile and mission.",
            textStyles: { fontSize: "12px", color: "#666", fontWeight: "normal" }
          }
        }
      ];
    }

    return [
      {
        type: CONTENT_BLOCK,
        data: { 
          name: "Highest Revenue Event Examples",
          content: "",
          textStyles: { fontSize: "12px", color: "#666", fontWeight: "normal" }
        }
      },
      {
        type: GET_HTML,
        data: { gethtml: renderTopProfitable }
      },
      {
        type: CONTENT_BLOCK,
        data: { 
          name: "Most Engaged Event Examples",
          content: "",
          textStyles: { fontSize: "12px", color: "#666", fontWeight: "normal" }
        }
      },
      {
        type: GET_HTML,
        data: { gethtml: renderTopEngaged }
      },
      {
        type: CONTENT_BLOCK,
        data: { 
          name: "Most Used Settings",
          content: `Target Children: ${superlativeData.mostUsedSettings.targetChildren ? "Yes" : "No"} | Application Required: ${superlativeData.mostUsedSettings.applicationRequired ? "Yes" : "No"} | Offline Payments: ${superlativeData.mostUsedSettings.offlinePayments ? "Yes" : "No"} | Share Transaction Fee: ${superlativeData.mostUsedSettings.shareTransactionFee ? "Yes" : "No"}`,
          textStyles: { fontSize: "12px", color: "#666", fontWeight: "normal" }
        }
      }
    ];
  };

  const getSettingsFields = () => [
    {
      type: SWITCH,
      data: {
        name: "Target Children",
        checked: addChild,
        required: false,
        handleClick: () => setAddChild(!addChild)
      }
    },
    {
      type: SWITCH,
      data: {
        name: "Offline Payments",
        checked: manualInvoices,
        required: false,
        info: "Allows your client to pass through checkout without paying online",
        handleClick: () => setManualInvoices(!manualInvoices)
      }
    },
    {
      type: SWITCH,
      data: {
        name: "Application Required",
        checked: applicationRequired,
        required: false,
        handleClick: () => setApplicationRequired(!applicationRequired)
      }
    },
    {
      type: SWITCH,
      data: {
        name: "Share Transaction Fee",
        checked: transactionCost,
        required: false,
        handleClick: () => setTransactionCost(!transactionCost),
        isDisabled: false,
        info: "A smaller percentage fee will be passed on to customers"
      }
    },
    { type: GET_HTML, data: { gethtml: eventTitleWithShuffle } },
    { type: GET_HTML, data: { gethtml: eventDescriptionWithShuffle } },
    { type: GET_HTML, data: { gethtml: eventImageWithShuffle } }
  ];

  const handleSubmit = async () => {
    let s3PhotoKey = null;
    setIsCreating(true);
    try {
      if (eventImage && /^https?:\/\//i.test(eventImage)) {
        const resp = await axios.post(
          `${process.env.REACT_APP_API}/partners/events/import-photo-from-url`,
          { imageUrl: eventImage }
        );
        if (resp.data?.success && resp.data.data?.event_image) {
          s3PhotoKey = resp.data.data.event_image;
        }
      }
    } catch (e) {
      console.error("Failed to import AI image to S3:", e);
    }
    
    setGlobal({
      event: {
        ...global.event,
        event_title: eventTitle,
        description: eventDescription,
        event_image: s3PhotoKey || global.event.event_image || null
      }
    });

    history.push({
      pathname: "/partners/events/setup",
      state: {
        addChild: addChild,
        manualInvoices: manualInvoices,
        applicationRequired: applicationRequired,
        allowSameEnrollment: allowSameEnrollment,
        birthdayEvent: birthdayEvent,
        transactionCost: transactionCost,
        fromAIModal: true  
      }
    });
    setIsCreating(false);
  };

  const getButtons = () => {
    const createButton = {
      name: isCreating ? "Creating..." : "Create",
      handleClick: handleSubmit,
      buttonColour: "#3B4EFF",
      textColour: "#fff",
      disabled: isCreating || isGeneratingSuggestions
    };
    const shuffleButton = {
      name: isGeneratingSuggestions ? "Shuffling..." : "Grand Shuffle",
      handleClick: shuffleSuggestions,
      buttonColour: "#6c757d",
      textColour: "#fff",
      disabled: isGeneratingSuggestions || isCreating
    };
    return [shuffleButton, createButton];
  };

  const getBaseModalProps = () => ({
    title: "AI Event Inspiration",
    fields: [...getSuperlativeFields(), ...getSettingsFields()],
    buttons: getButtons(),
    handleClose: onClose,
    height: "85vh",
    midSectionHeight: "58vh"
  });

  return (
    <>
      <BaseModal {...getBaseModalProps()} />
      {showReshuffleModal && (
        <ReshuffleModal
          onClose={() => setShowReshuffleModal(false)}
          onApply={reshuffleMode === "name" ? handleApplyGeneratedName : handleApplyGeneratedDescription}
          currentValue={reshuffleMode === "name" ? eventTitle : eventDescription}
          mode={reshuffleMode}
          listingType="events"
        />
      )}
    </>
  );
};

export default AIEventInspirationModal;
